import {authorized, optionallyAuthorized} from "./ApiClient";
import {send} from "./http";
import {NewsCampaign, NewsCampaignBindingModel, NewsSignupSource, NewsSubscriberView, NewsSubscription} from "./models";

// Which wording of the sign-up form (src/i18n/en/news.ts, news.dialog) someone agreed to: change it with any change to
// what that says they're signing up for. The API keeps it with each sign-up.
export const NEWS_CONSENT_VERSION = '2026-10-18';

export interface NewsSignup {
    sku: string;
    news: boolean;
    source: NewsSignupSource;
    // Signed out: the address, and Turnstile's token. Signed in, the account's address is used.
    email?: string;
    turnstileToken?: string;
}

const linkQuery = (id: string, token: string) => `id=${encodeURIComponent(id)}&token=${encodeURIComponent(token)}`;

// The mailing list: signing up to hear when a product is released (double opt-in: signed out, or with an address that
// isn't confirmed, the API emails a link to confirm it), the confirmation and unsubscribe links in its emails, and for
// administrators, its subscribers and the emails sent to them.
export const NewsApi = {
    // 'pending' when a confirmation link has been emailed (always, signed out, whatever the address); 'confirmed' when
    // the signed-in account's address was added at once.
    subscribe: (signup: NewsSignup) =>
        optionallyAuthorized<{ status: 'pending' | 'confirmed' }>('POST', '/news/subscribe', { ...signup, consentVersion: NEWS_CONSENT_VERSION }),

    // The confirmation email's link: 404 when it's wrong or has expired. Returns what the address now gets.
    confirm: (token: string) => send<{ products: { sku: string, name: string }[], news: boolean }>('POST', '/news/confirm', { token }),

    // An unsubscribe link's subscription: 404 when it's wrong, or they've gone.
    subscription: (id: string, token: string) => send<NewsSubscription>('GET', `/news/subscription?${linkQuery(id, token)}`),

    // 'news' stops only the other news; 'all', everything.
    unsubscribe: (id: string, token: string, scope: 'all' | 'news') =>
        send<void>('POST', `/news/unsubscribe?${linkQuery(id, token)}`, { scope }),

    // Administrators.
    subscribers: () => authorized<{ subscribers: NewsSubscriberView[], suppressed: number }>('GET', '/news/subscriber'),
    deleteSubscriber: (id: string) => authorized<void>('DELETE', `/news/subscriber/${encodeURIComponent(id)}`),
    campaigns: () => authorized<NewsCampaign[]>('GET', '/news/campaign'),
    campaign: (id: string) => authorized<NewsCampaign>('GET', `/news/campaign/${encodeURIComponent(id)}`),
    createCampaign: (campaign: NewsCampaignBindingModel) => authorized<NewsCampaign>('POST', '/news/campaign', campaign),
    updateCampaign: (id: string, campaign: NewsCampaignBindingModel) => authorized<NewsCampaign>('PUT', `/news/campaign/${encodeURIComponent(id)}`, campaign),
    deleteCampaign: (id: string) => authorized<void>('DELETE', `/news/campaign/${encodeURIComponent(id)}`),
    // Emails the draft to the signed-in administrator; sending needs a test of what it says now.
    testCampaign: (id: string) => authorized<NewsCampaign>('POST', `/news/campaign/${encodeURIComponent(id)}/test`),
    // Starts sending in the background (or starts it again, if it stopped): its sent count goes up as it does.
    sendCampaign: (id: string) => authorized<NewsCampaign>('POST', `/news/campaign/${encodeURIComponent(id)}/send`)
};
